import React, { useState } from 'react';

const ManageLabelsModal = ({ labels, onSave, onClose }) => {
  const [items, setItems] = useState(labels.map((l, i) => ({ id: i, text: l })));
  const [newLabel, setNewLabel] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');

  const addLabel = () => {
    const trimmed = newLabel.trim();
    if (!trimmed || items.some(i => i.text === trimmed)) return;
    setItems(prev => [...prev, { id: Date.now(), text: trimmed }]);
    setNewLabel('');
  };

  const removeLabel = (id) => {
    setItems(prev => prev.filter(i => i.id !== id));
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditText(item.text);
  };

  const saveEdit = () => {
    const trimmed = editText.trim();
    if (!trimmed) return;
    setItems(prev => prev.map(i => i.id === editingId ? { ...i, text: trimmed } : i));
    setEditingId(null);
    setEditText('');
  };

  const moveUp = (index) => {
    if (index === 0) return;
    setItems(prev => {
      const arr = [...prev];
      [arr[index - 1], arr[index]] = [arr[index], arr[index - 1]];
      return arr;
    });
  };

  const moveDown = (index) => {
    if (index >= items.length - 1) return;
    setItems(prev => {
      const arr = [...prev];
      [arr[index], arr[index + 1]] = [arr[index + 1], arr[index]];
      return arr;
    });
  };

  const handleSave = () => {
    onSave(items.map(i => i.text));
  };

  return (
    <>
      <div className="modal-backdrop" onClick={onClose}></div>
      <div className="modal-content fade-in" style={{ maxWidth: '480px' }} data-testid="manage-labels-modal">
        <h2 style={{ 
          fontFamily: 'Playfair Display, serif', 
          fontSize: '22px', 
          marginBottom: '8px',
          textAlign: 'center'
        }}>
          Manage Section Labels
        </h2>
        <p style={{ fontSize: '13px', color: 'var(--kahla-text-light)', textAlign: 'center', marginBottom: '20px' }}>
          Add, rename, reorder, or remove labels for your moodboards.
        </p>

        {/* Label list */}
        <div style={{ marginBottom: '16px' }}>
          {items.map((item, index) => (
            <div 
              key={item.id}
              style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                padding: '8px 12px', marginBottom: '6px',
                border: '1px solid var(--kahla-border)',
                backgroundColor: 'var(--kahla-cream)',
              }}
              data-testid={`label-item-${index}`}
            >
              {/* Reorder arrows */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <button 
                  onClick={() => moveUp(index)} 
                  disabled={index === 0}
                  style={{ 
                    background: 'none', border: 'none', cursor: index === 0 ? 'default' : 'pointer',
                    fontSize: '10px', padding: '0 2px', opacity: index === 0 ? 0.3 : 1,
                    color: 'var(--kahla-text-light)'
                  }}
                  data-testid={`label-move-up-${index}`}
                >&#9650;</button>
                <button 
                  onClick={() => moveDown(index)} 
                  disabled={index >= items.length - 1}
                  style={{ 
                    background: 'none', border: 'none', cursor: index >= items.length - 1 ? 'default' : 'pointer',
                    fontSize: '10px', padding: '0 2px', opacity: index >= items.length - 1 ? 0.3 : 1,
                    color: 'var(--kahla-text-light)'
                  }}
                  data-testid={`label-move-down-${index}`}
                >&#9660;</button>
              </div>

              {/* Label text or edit input */}
              {editingId === item.id ? (
                <input 
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEditingId(null); }}
                  autoFocus
                  style={{ 
                    flex: 1, padding: '4px 8px', fontSize: '14px',
                    border: '1px solid var(--kahla-charcoal)', outline: 'none',
                  }}
                  data-testid={`label-edit-input-${index}`}
                />
              ) : (
                <span 
                  style={{ flex: 1, fontSize: '14px', color: 'var(--kahla-text)' }}
                  onDoubleClick={() => startEdit(item)}
                >
                  {item.text}
                </span>
              )}

              {/* Action buttons */}
              {editingId === item.id ? (
                <button 
                  onClick={saveEdit}
                  style={{ 
                    background: 'none', border: 'none', cursor: 'pointer',
                    fontSize: '13px', color: 'var(--kahla-charcoal)', fontWeight: '600'
                  }}
                >Save</button>
              ) : (
                <>
                  <button 
                    onClick={() => startEdit(item)}
                    style={{ 
                      background: 'none', border: 'none', cursor: 'pointer',
                      fontSize: '12px', color: 'var(--kahla-text-light)'
                    }}
                    data-testid={`label-edit-${index}`}
                  >Edit</button>
                  <button 
                    onClick={() => removeLabel(item.id)}
                    style={{ 
                      background: 'none', border: 'none', cursor: 'pointer',
                      fontSize: '12px', color: '#C75050'
                    }}
                    data-testid={`label-delete-${index}`}
                  >Delete</button>
                </>
              )}
            </div>
          ))}
          {items.length === 0 && (
            <p style={{ fontSize: '13px', color: 'var(--kahla-text-light)', textAlign: 'center', padding: '12px' }}>
              No labels yet. Add one below.
            </p>
          )}
        </div>

        {/* Add new label */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
          <input 
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addLabel(); }}
            placeholder="New label name..."
            style={{ 
              flex: 1, padding: '8px 12px', fontSize: '14px',
              border: '1px solid var(--kahla-border)', outline: 'none',
            }}
            data-testid="new-label-input"
          />
          <button 
            className="btn-primary"
            onClick={addLabel}
            disabled={!newLabel.trim()}
            data-testid="add-label-btn"
          >
            Add
          </button>
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={handleSave} data-testid="save-labels-btn">
            Save Labels
          </button>
        </div>
      </div>
    </>
  );
};

export default ManageLabelsModal;
